import React, { useEffect, useRef, useState } from "react";
import { motion, useInView } from "framer-motion";
import { Briefcase, Lightbulb, TrendingUp } from "lucide-react";

function AnimatedCounter({ value, suffix = "", duration = 2 }) {
  const [count, setCount] = useState(0);
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-50px" });
  const numValue = parseInt(value);

  useEffect(() => {
    if (!isInView) return;
    let start = 0;
    const end = numValue;
    const stepTime = (duration * 1000) / end;
    const timer = setInterval(() => {
      start += 1;
      setCount(start);
      if (start >= end) clearInterval(timer);
    }, stepTime);
    return () => clearInterval(timer);
  }, [isInView, numValue, duration]);

  return <span ref={ref}>{count}{suffix}</span>;
}

const stats = [
  { icon: Briefcase, value: 15, suffix: "+", label: "Years Building", ariaLabel: "Over 15 years of building companies" },
  { icon: Lightbulb, value: 11, suffix: "+", label: "Companies", ariaLabel: "Over 11 companies founded or invested in" },
  { icon: TrendingUp, value: 3, suffix: "", label: "Acquired", ariaLabel: "3 companies successfully acquired" },
];

const fadeInUp = {
  hidden: { opacity: 0, y: 50 },
  visible: (i) => ({
    opacity: 1,
    y: 0,
    transition: { delay: i * 0.12, duration: 0.8, ease: [0.22, 1, 0.36, 1] },
  }),
};

export default function About() {
  return (
    <section
      id="about"
      data-testid="about-section"
      className="relative py-32 md:py-44 px-6 md:px-12 lg:px-24 overflow-hidden"
      aria-labelledby="about-title"
    >
      <div className="gradient-orb gradient-orb-2 top-[10%] left-[-5%]" aria-hidden="true" />

      <div className="max-w-6xl mx-auto relative z-10">
        {/* Section label */}
        <motion.div
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: "-100px" }}
          variants={fadeInUp}
          custom={0}
          className="flex items-center gap-4 mb-8"
        >
          <div className="w-8 h-[1px] bg-white/20" />
          <p
            className="text-[11px] uppercase tracking-[0.3em] text-white/40"
            style={{ fontFamily: "system-ui, sans-serif" }}
            data-testid="about-label"
          >
            About
          </p>
        </motion.div>

        {/* Heading */}
        <motion.h2
          id="about-title"
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: "-100px" }}
          variants={fadeInUp}
          custom={1}
          className="text-3xl md:text-5xl lg:text-6xl text-white leading-[1.15] mb-16 max-w-4xl"
          data-testid="about-heading"
        >
          I've spent over 15 years building companies that help people share their{" "}
          <span className="italic text-white/60">knowledge, creativity</span> and{" "}
          <span className="italic text-white/60">passion</span> with the world.
        </motion.h2>

        {/* Body text */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20 mb-24">
          <motion.p
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: "-100px" }}
            variants={fadeInUp}
            custom={2}
            className="text-sm md:text-base text-white/40 leading-[1.9]"
            style={{ fontFamily: "system-ui, sans-serif" }}
            data-testid="about-text-1"
          >
            As an entrepreneur, advisor and investor, I work with people and companies 
            that have the potential to bring joy to themselves or others through their passion. 
            Whether you are helping people play video games and connect with a community, 
            or you have a talent to share with the world &mdash; I'd like to invest, advise 
            or work with you.
          </motion.p>
          <motion.p
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: "-100px" }}
            variants={fadeInUp}
            custom={3}
            className="text-sm md:text-base text-white/40 leading-[1.9]"
            style={{ fontFamily: "system-ui, sans-serif" }}
            data-testid="about-text-2"
          >
            My portfolio spans creator tools, esports, digital health, film production, 
            and AI-powered technologies. I've been fortunate to see several of my companies 
            acquired by industry leaders like Logitech and XSplit, and I continue 
            to build and invest in the next generation of transformative companies.
          </motion.p>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 md:gap-6" role="list" aria-label="Key statistics">
          {stats.map((stat, i) => (
            <motion.div
              key={stat.label}
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true, margin: "-50px" }}
              variants={fadeInUp}
              custom={i + 4}
              className="stat-shimmer glass-card rounded-2xl p-8 md:p-10 neon-hover transition-all duration-500 group"
              data-testid={`stat-card-${stat.label.toLowerCase().replace(/\s+/g, '-')}`}
              role="listitem"
              aria-label={stat.ariaLabel}
            >
              <div className="flex items-start justify-between mb-6">
                <stat.icon className="w-5 h-5 text-white/30 group-hover:text-white/60 transition-colors" aria-hidden="true" />
                <span className="text-[10px] uppercase tracking-[0.15em] text-white/15" style={{ fontFamily: "system-ui, sans-serif" }}>
                  {String(i + 1).padStart(2, '0')}
                </span>
              </div>
              <p className="text-4xl md:text-5xl lg:text-6xl font-normal tracking-tight mb-3 text-white italic">
                <AnimatedCounter value={stat.value} suffix={stat.suffix} />
              </p>
              <p className="text-xs uppercase tracking-[0.15em] text-white/30" style={{ fontFamily: "system-ui, sans-serif" }}>
                {stat.label}
              </p>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}
